import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from "react-native";
import { useAtom, useAtomValue } from "jotai";
import { modalStackAtom, navModalStackAtom, ModalInstance } from "../atoms/modal";
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useTheme } from "../hooks/use-theme";
import IconButton from "./IconButton";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import {
    getModalStackPosition,
    getModalStackSlot,
    STACK_ENTER_TRANSLATE_Y,
    WEB_DIALOG_ANIMATION_DURATION,
} from "../utils/modal-stack-slot";
import { useModalDialogBoxStyle } from "./ModalDialogFrame";

// ModalPortal renders JS modals on WEB only — native platforms present a real RN Modal in place (see
// Modal.tsx). Desktop web (`isLarge`) shows a centered dialog with a quicker fade+scale, matching the
// react-navigation modal-screen look; narrow/mobile web keeps a full-height slide-up. Dialog
// dimensions/timing come from utils/modal-stack-slot.ts (shared with the navigation modals).
const FULLSCREEN_ANIMATION_DURATION = 500;

// Stacked dialogs are nudged up + scaled down per depth-from-top so the ones behind peek out, like
// stacked iOS sheets. The slot geometry (front peek, per-depth offset/scale, hide-from-depth-2) lives
// in utils/modal-stack-slot.ts, shared with the navigation modals (hooks/use-modal-recede-style.ts)
// so both engines stack into one coherent window.
// One shared dim scrim behind the whole stack (stays up while any modal is open, so opening another
// doesn't flash the page), plus a per-depth dim applied directly on the modals behind the front one.
const SCRIM_OPACITY = 0.4;
const DIM_PER_DEPTH = 0.18;
const MAX_DIM = 0.35;

// Single persistent dark scrim shared by the whole stack. It fades in when the first modal opens and
// out when the last closes — it does NOT re-animate when an intermediate modal opens/closes, avoiding
// the brief "page brightens" flash of a per-modal backdrop. Tapping it closes the front modal.
const StackScrim: React.FC<{ visible: boolean; onPress?: () => void }> = ({ visible, onPress }) => {
    const opacity = useSharedValue(0);
    useEffect(() => {
        opacity.value = withTiming(visible ? SCRIM_OPACITY : 0, {
            duration: WEB_DIALOG_ANIMATION_DURATION,
        });
    }, [visible, opacity]);
    const style = useAnimatedStyle(() => ({ opacity: opacity.value }));
    return (
        <Animated.View style={[StyleSheet.absoluteFill, { backgroundColor: "black" }, style]}>
            <Pressable style={StyleSheet.absoluteFill} onPress={onPress} />
        </Animated.View>
    );
};

type ModalCellProps = {
    modal: ModalInstance;
    // 0 for the front-most modal, increasing for the ones behind it.
    depthFromTop: number;
    // True when another modal (JS or navigation) sits below this one — it then enters/exits with an
    // upward slide instead of the scale pop the first modal uses.
    stacked: boolean;
    onRemove: (id: string) => void;
};

const ModalCell: React.FC<ModalCellProps> = ({ modal, depthFromTop, stacked, onRemove }) => {
    const { colors, marginSize } = useTheme();
    // The centered dialog reuses the SAME box (size, radius, shadow) as the navigation modals.
    const dialogBoxStyle = useModalDialogBoxStyle();
    const { height } = useWindowDimensions();
    // `isLarge` is forced false on tablets/phones and only true on wide (desktop web) viewports, so
    // the centered-dialog branch is effectively web-only; native always takes the full-screen slide.
    const isLarge = useIsScreenSizeMin("large");
    const insets = useSafeAreaInsets();

    // Use progress (0 = hidden, 1 = visible) instead of absolute translateY
    // This avoids issues with height changes during animation
    const progress = useSharedValue(0);

    const hideContainerBackground = modal.hideContainerBackground ?? false;
    const fitContent = modal.fitContent ?? false;
    const duration = isLarge ? WEB_DIALOG_ANIMATION_DURATION : FULLSCREEN_ANIMATION_DURATION;

    const onRemoveRef = useRef(onRemove);
    useEffect(() => {
        onRemoveRef.current = onRemove;
    }, [onRemove]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        if (modal.visible) {
            progress.value = withTiming(1, {
                duration,
                easing: Easing.out(Easing.cubic),
            });
        } else {
            progress.value = withTiming(0, {
                duration,
                easing: Easing.in(Easing.cubic),
            });
            // Remove after animation completes
            timeoutId = setTimeout(() => {
                onRemoveRef.current(modal.id);
            }, duration);
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [modal.visible, modal.id, progress, duration]);

    // Full-screen slide (native / mobile web).
    const slideStyle = useAnimatedStyle(() => ({
        transform: [{ translateY: (1 - progress.value) * height }],
    }));

    // Stack-slot target for this modal's depth, animated so the ones behind smoothly slide up/scale
    // down (and, from depth 2, slide back down to hide) as modals open/close. The geometry is shared
    // with the navigation modals via getModalStackSlot. iOS-sheet-like sliding window: only the front
    // (depth 0) and the one behind it (depth 1) are visible; from depth 2 the dialog slides back DOWN
    // to the front slot — hidden behind the opaque front — so the oldest visible one "descends" out
    // of view as a new modal opens.
    const slot = getModalStackSlot(depthFromTop);
    const slotTranslateY = useSharedValue(slot.translateY);
    const slotScale = useSharedValue(slot.scale);
    // Darkens a modal directly once it's behind the front one (depth ≥ 1).
    const dimTarget = Math.min(depthFromTop * DIM_PER_DEPTH, MAX_DIM);
    const dim = useSharedValue(dimTarget);
    useEffect(() => {
        slotTranslateY.value = withTiming(slot.translateY, {
            duration: WEB_DIALOG_ANIMATION_DURATION,
        });
        slotScale.value = withTiming(slot.scale, { duration: WEB_DIALOG_ANIMATION_DURATION });
        dim.value = withTiming(dimTarget, { duration: WEB_DIALOG_ANIMATION_DURATION });
    }, [slot.translateY, slot.scale, dimTarget, slotTranslateY, slotScale, dim]);

    const dialogStyle = useAnimatedStyle(() => {
        // First modal: scale pop (0.96 → 1). Stacked modal (2nd+): rise into its slot from
        // STACK_ENTER_TRANSLATE_Y below, no scale (and descend back on close).
        const enterScale = stacked ? 1 : 0.96 + progress.value * 0.04;
        const translateY = stacked
            ? slotTranslateY.value + (1 - progress.value) * STACK_ENTER_TRANSLATE_Y
            : slotTranslateY.value * progress.value;
        return {
            opacity: progress.value,
            transform: [{ translateY }, { scale: enterScale * slotScale.value }],
        };
    });

    const dimStyle = useAnimatedStyle(() => ({ opacity: dim.value }));

    const bgColor = modal.bgColorName !== undefined ? colors[modal.bgColorName] : undefined;
    const backgroundColor = bgColor ? bgColor : hideContainerBackground ? "transparent" : "white";

    const styles = useMemo(
        () =>
            StyleSheet.create({
                // Dark overlay laid directly over a receded modal's content (clipped to its rounded
                // shape by the dialog's overflow:hidden).
                dimOverlay: {
                    ...StyleSheet.absoluteFill,
                    backgroundColor: "black",
                },
                // Full-screen container (native / mobile web). `edges.top: "off"` opts out of the
                // status-bar inset (e.g. DocumentEditor); other consumers keep the top inset.
                container: {
                    flex: 1,
                    paddingTop: modal.edges?.top === "off" ? 0 : insets.top,
                },
                // Centers the dialog; box-none so taps outside it fall through to the backdrop. No
                // padding: the dialog's "85%" height must resolve against the full screen so it
                // matches the navigation modals (ModalScreenWrapper / CreateMissionStack), which
                // center via `margin: auto` in a full-screen card.
                dialogPositioner: {
                    ...StyleSheet.absoluteFill,
                    alignItems: "center",
                    justifyContent: "center",
                },
                closeButton: {
                    position: "absolute",
                    top: marginSize + insets.top,
                    right: marginSize,
                },
            }),
        [marginSize, insets.top, modal.edges?.top],
    );

    let children = modal.children;
    if (modal.onClose && !modal.hideCloseButton) {
        children = (
            <>
                {children}
                <IconButton style={styles.closeButton} onPress={modal.onClose} icon="close" />
            </>
        );
    }

    if (isLarge) {
        return (
            <View style={styles.dialogPositioner} pointerEvents="box-none">
                <Animated.View
                    style={[
                        dialogBoxStyle,
                        // `fitContent` modals size to their content instead of the fixed 85% height.
                        fitContent ? { height: "auto" as const } : null,
                        dialogStyle,
                        { backgroundColor },
                    ]}
                >
                    {children}
                    <Animated.View style={[styles.dimOverlay, dimStyle]} pointerEvents="none" />
                </Animated.View>
            </View>
        );
    }

    return (
        <Animated.View style={[styles.container, slideStyle, { backgroundColor }]}>
            {children}
        </Animated.View>
    );
};

const ModalPortal: React.FC = () => {
    const [modalStack, setModalStack] = useAtom(modalStackAtom);
    const isLarge = useIsScreenSizeMin("large");
    // Navigation modals (e.g. CreateMissionStack) sit below all JS modals in the single conceptual
    // stack; count them so each JS modal's depth/stacked is computed against the whole stack.
    const navModalCount = useAtomValue(navModalStackAtom).length;

    const handleRemove = useCallback(
        (id: string) => {
            setModalStack((prev) => prev.filter((m) => m.id !== id));
        },
        [setModalStack],
    );

    if (modalStack.length === 0) {
        return null;
    }

    const anyVisible = modalStack.some((m) => m.visible);
    const visibleCount = modalStack.filter((m) => m.visible).length;
    // Topmost visible modal — the shared scrim's tap closes it.
    const frontOnClose = [...modalStack].reverse().find((m) => m.visible)?.onClose;

    // Web only: native platforms present a real RN Modal in place (see Modal.tsx). `depthFromTop`
    // drives the stacked-dialog slide, the shared scrim dims the page, and each receded modal is
    // dimmed directly.
    return (
        <View
            style={[
                StyleSheet.absoluteFill,
                {
                    overflow: Platform.OS === "web" ? ("clip" as "hidden") : "hidden",
                    pointerEvents: "box-none",
                },
            ]}
        >
            {isLarge && <StackScrim visible={anyVisible} onPress={frontOnClose} />}
            {modalStack.map((modal, index) => {
                // Position in the single conceptual stack (nav modals below all JS modals), counting
                // only VISIBLE modals so a modal starting to close frees its slot immediately. Shared
                // getModalStackPosition drives both the recede depth and slide-vs-scale — identical to
                // the navigation modals (hooks/use-modal-recede-style.ts).
                const jsVisibleBelow = modalStack.slice(0, index).filter((m) => m.visible).length;
                const { depthFromTop, stacked } = getModalStackPosition(
                    navModalCount + jsVisibleBelow,
                    navModalCount + visibleCount,
                );
                return (
                    <ModalCell
                        key={modal.id}
                        modal={modal}
                        depthFromTop={depthFromTop}
                        stacked={stacked}
                        onRemove={handleRemove}
                    />
                );
            })}
        </View>
    );
};

export default ModalPortal;
